import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { setAuthTokenGetter, setOnUnauthorized, setTenantIdGetter } from '@/providers/apiDataProvider'

const TENANT_KEY = 'rosta_tenant_id'

// Set token getter synchronously on module load from localStorage
const initialToken = localStorage.getItem('rosta_token')
if (initialToken) {
  setAuthTokenGetter(() => initialToken)
}
const initialTenantId = localStorage.getItem(TENANT_KEY)
if (initialTenantId) {
  setTenantIdGetter(() => initialTenantId)
}

declare global {
  interface Window {
    __ROSTA_API_URL__?: string;
    __ROSTA_KEYCLOAK_URL__?: string;
  }
}

interface AuthUser {
  name: string
  email: string
  avatar?: string
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  token: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_KEY = 'rosta_auth'
const TOKEN_KEY = 'rosta_token'

const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'rosta'
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'rosta-web-ui'
const API_URL = window.__ROSTA_API_URL__
  || import.meta.env.VITE_API_URL
  || 'http://localhost:3200/api/v1'

// Use same-origin proxy when on Vite dev server (port 5173), otherwise direct to Keycloak
const isViteDev = typeof window !== 'undefined' && window.location.port === '5173'
const KEYCLOAK_URL = window.__ROSTA_KEYCLOAK_URL__
  || import.meta.env.VITE_KEYCLOAK_URL
  || (isViteDev ? '' : 'http://localhost:8080')

function loadFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function loadToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function saveToStorage(user: AuthUser | null) {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

function saveToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => loadFromStorage())
  const [token, setToken] = useState<string | null>(() => loadToken())

  // Register 401 handler — clear session and show login page
  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(null)
      setToken(null)
      setAuthTokenGetter(() => null)
      setTenantIdGetter(() => null)
      saveToStorage(null)
      saveToken(null)
      localStorage.removeItem(TENANT_KEY)
    })
    return () => setOnUnauthorized(null)
  }, [])

  // Keep auth token getter in sync with token state
  useEffect(() => {
    setAuthTokenGetter(() => token)
  }, [token])

const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch(
        `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'password',
            client_id: KEYCLOAK_CLIENT_ID,
            username: email,
            password: password,
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Login failed')
      }

      const data = await response.json()
      const kcToken = data.access_token

      // Decode KC JWT to get user info
      const payload = JSON.parse(atob(kcToken.split('.')[1]))
      const newUser: AuthUser = {
        name: payload.name || payload.preferred_username || email,
        email: payload.email || email,
      }

      // Exchange KC JWT for platform session token
      const sessionRes = await fetch(`${API_URL}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: kcToken }),
      })

      if (!sessionRes.ok) {
        const err = await sessionRes.json().catch(() => ({}))
        throw new Error(err.detail || 'Session creation failed')
      }

      const sessionData = await sessionRes.json()
      const platformToken = sessionData.data.sessionToken

      const tid = sessionData.data?.session?.currentTenant?.id || null
      setUser(newUser)
      setToken(platformToken)
      // Set token getter SYNCHRONOUSLY so data provider has it before Admin mounts
      setAuthTokenGetter(() => platformToken)
      setTenantIdGetter(() => tid)
      saveToStorage(newUser)
      saveToken(platformToken)
      if (tid) localStorage.setItem(TENANT_KEY, tid)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }, [])

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    setTenantIdGetter(() => null)
    saveToStorage(null)
    saveToken(null)
    localStorage.removeItem(TENANT_KEY)
  }, [])

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: user !== null, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
